// Matematica de la cronologia del cuaderno: el dia visto por dentro.
//
// Igual que timelineScale, funciones puras sin React para poder probarlas con
// node. Aquella cronologia mide DIAS de un proyecto; esta mide MINUTOS de una
// jornada, que es otro problema: lo que interesa no es cuanto dura una tarea
// sino con cuales se pisa.

// Una jornada dibujada entera (00:00-24:00) deja el trabajo apelotonado en la
// franja central, asi que el eje se recorta a lo que de verdad hay. Estos son
// el suelo y el techo cuando no hay nada que recortar.
export const HORA_MIN_DEF = 8;
export const HORA_MAX_DEF = 18;

export const MIN_DIA = 24 * 60;

// "HH:MM", "HH:MM:SS" o "HH.MM" -> minutos desde medianoche. null si no hay
// hora legible: una entrada a medias no debe colarse en el eje como las 00:00.
export function minutosDe(hhmm) {
  if (hhmm === null || hhmm === undefined) return null;
  const m = String(hhmm).trim().match(/^(\d{1,2})[:.h](\d{2})/);
  if (!m) return null;
  const h = Number(m[1]), mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

export const fmtHora = min => {
  const v = Math.max(0, Math.min(MIN_DIA, Math.round(min)));
  return String(Math.floor(v / 60) % 24).padStart(2, "0") + ":" + String(v % 60).padStart(2, "0");
};

const dia = s => String(s || "").slice(0, 10);

// El dia al que cuelga una entrada en el cuaderno. Mismo criterio que la
// pestaña Registro: entry_date manda y start_date es el repuesto.
export const diaDe = a => dia(a.entry_date) || dia(a.start_date);

// El tramo que ocupa una entrada DENTRO de un dia concreto, en minutos.
//
// Devuelve null si la entrada no tiene las dos horas: sin hora de fin no hay
// barra que pintar, solo un punto, y un punto no dice nada de los solapes.
//
// Una entrada que empieza un dia y acaba otro se recorta al dia pedido; lo que
// queda fuera se marca con cortaAntes/cortaDespues para poder dibujar el borde
// abierto y no mentir diciendo que acabo a las 23:59.
export function tramoDelDia(a, iso) {
  const ini = minutosDe(a.start_time), fin = minutosDe(a.end_time);
  if (ini === null || fin === null) return null;

  const d0 = dia(a.start_date) || diaDe(a);
  const d1 = dia(a.end_date) || d0;
  if (!d0 || !iso) return null;
  if (iso < d0 || iso > d1) return null;

  // Sin fecha de fin distinta, un fin anterior al inicio es una jornada que
  // cruza la medianoche (22:00 -> 01:00), no un error de tecleo. Estrictamente
  // anterior: 09:00 -> 09:00 es una entrada a medio rellenar, no 15 h de turno.
  const cruzaMedianoche = d0 === d1 && fin < ini;
  const desde = iso === d0 ? ini : 0;
  const hasta = iso === d1 && !cruzaMedianoche ? fin : MIN_DIA;

  if (hasta <= desde) return null;
  return {
    ini: desde,
    fin: hasta,
    cortaAntes: iso > d0,
    cortaDespues: (iso < d1) || cruzaMedianoche,
  };
}

// El peso de productividad cuando no se sabe: el mismo neutro que usa el resto
// del cuaderno (AG_PROD_DEFAULT). Se repite aqui como numero suelto a proposito
// para que este modulo siga sin dependencias.
export const PROD_NEUTRA = 50;

// Las entradas de un dia con tramo dibujable, ordenadas por hora de inicio.
//
// prodDe traduce una entrada a su peso de productividad (0-100). Se pasa desde
// fuera en vez de importar getTipoProd para que el modulo no arrastre utils ni
// constants y se pueda ejecutar con node a pelo.
//
// A igual hora de inicio manda la mas productiva, y solo despues la mas larga:
// el desempate por duracion dejaba arriba la tarea de fondo aunque fuese la que
// menos aporta.
export function tramosDelDia(entradas, iso, prodDe = null) {
  return (entradas || [])
    .map(a => {
      const t = tramoDelDia(a, iso);
      if (!t) return null;
      const prod = prodDe ? prodDe(a) : PROD_NEUTRA;
      return { entrada: a, prod: Number.isFinite(prod) ? prod : PROD_NEUTRA, ...t };
    })
    .filter(Boolean)
    .sort((x, y) => x.ini - y.ini || y.prod - x.prod || (y.fin - y.ini) - (x.fin - x.ini));
}

// Las entradas del dia que NO se pueden situar en el eje. No se tiran: se
// cuentan aparte, porque una cronologia que esconde la mitad del dia sin
// avisar hace pensar que esa mitad no existe.
export function sinHorario(entradas, iso) {
  return (entradas || []).filter(a => diaDe(a) === iso && !tramoDelDia(a, iso));
}

const seSolapan = (a, b) => a.ini < b.fin && b.ini < a.fin;

// Racimos: grupos de tramos encadenados por solape. Dos racimos no comparten
// ni un minuto, y eso es lo que permite recolocar los carriles de uno sin tocar
// los del otro.
export function racimos(tramos) {
  const orden = [...tramos].sort((a, b) => a.ini - b.ini);
  const grupos = [];
  let actual = null, finActual = -1;
  orden.forEach(t => {
    if (!actual || t.ini >= finActual) { actual = [t]; grupos.push(actual); finActual = t.fin; }
    else { actual.push(t); finActual = Math.max(finActual, t.fin); }
  });
  return grupos;
}

// Sube al carril de arriba lo que mas aporta.
//
// Solo PERMUTA las etiquetas de los carriles dentro de cada racimo, no reasigna
// nada: los tramos de un mismo carril no se tocan entre si, asi que cambiarles
// el numero entre ellos siempre da un reparto valido y con los mismos carriles.
// Reordenar de verdad por productividad podria necesitar un carril de mas, y un
// dia mas alto por un criterio de lectura no compensa.
//
// El peso de un carril es la media de productividad ponderada por minutos: un
// carril con dos horas de trabajo de alto valor pesa mas que uno con diez
// minutos, aunque los diez minutos sean de un tipo mejor puntuado.
export function ordenarCarrilesPorProductividad(tramos) {
  const mapa = new Map(tramos.map(t => [t, t.carril]));

  racimos(tramos).forEach(grupo => {
    const usados = [...new Set(grupo.map(t => mapa.get(t)))].sort((a, b) => a - b);
    if (usados.length < 2) return;

    const peso = carril => {
      const suyos = grupo.filter(t => mapa.get(t) === carril);
      const min = suyos.reduce((s, t) => s + (t.fin - t.ini), 0);
      if (!min) return PROD_NEUTRA;
      return suyos.reduce((s, t) => s + t.prod * (t.fin - t.ini), 0) / min;
    };
    const inicio = carril =>
      Math.min(...grupo.filter(t => mapa.get(t) === carril).map(t => t.ini));

    // A igual peso decide la hora: sin este desempate el orden dependeria de
    // como viniera la lista y la vista bailaria entre recargas.
    const nuevo = [...usados]
      .sort((a, b) => peso(b) - peso(a) || inicio(a) - inicio(b) || a - b);

    const destino = new Map(nuevo.map((viejo, i) => [viejo, usados[i]]));
    grupo.forEach(t => mapa.set(t, destino.get(mapa.get(t))));
  });

  return tramos.map(t => ({ ...t, carril: mapa.get(t) }));
}

// Reparto en carriles: el primero que quede libre.
//
// Coloreado codicioso del grafo de intervalos. Recorriendo por hora de inicio
// da el minimo numero de carriles posible, que es justo lo que se quiere: el
// alto de la fila es entonces la medida de cuanto se pisa el dia.
//
// Con el reparto hecho, los carriles de cada racimo se recolocan para que lo
// mas productivo quede arriba.
export function repartirEnCarriles(tramos) {
  const finDeCarril = [];
  const puestos = tramos.map(t => {
    let carril = finDeCarril.findIndex(f => f <= t.ini);
    if (carril === -1) { carril = finDeCarril.length; finDeCarril.push(t.fin); }
    else finDeCarril[carril] = Math.max(finDeCarril[carril], t.fin);
    return { ...t, carril };
  });
  return {
    tramos: ordenarCarrilesPorProductividad(puestos),
    carriles: Math.max(1, finDeCarril.length),
  };
}

// Con cuantas otras se pisa cada tramo. Es el dato que da sentido a la vista,
// asi que va en el tramo y no se recalcula al pintar.
export function marcarSolapes(tramos) {
  return tramos.map(t => ({
    ...t,
    solapaCon: tramos.filter(o => o !== t && seSolapan(t, o)).length,
  }));
}

// Los islotes de tiempo sin ninguna tarea encima, dentro del rango pintado.
// Un hueco de 10 min entre dos reuniones es ruido; el minimo lo deja fuera.
export function huecos(tramos, desde, hasta, minimo = 15) {
  const orden = [...tramos].sort((a, b) => a.ini - b.ini);
  const fuera = [];
  let cursor = desde;
  orden.forEach(t => {
    if (t.ini > cursor) fuera.push({ ini: cursor, fin: Math.min(t.ini, hasta) });
    cursor = Math.max(cursor, t.fin);
  });
  if (cursor < hasta) fuera.push({ ini: cursor, fin: hasta });
  return fuera.filter(h => h.fin - h.ini >= minimo && h.ini < hasta);
}

// El rango de horas que se pinta: lo que ocupan las tareas y nada mas,
// redondeado a horas enteras.
//
// Antes esto tenia el horario de oficina (8-18) como suelo y techo fijos, con
// la idea de poder comparar dos dias de un vistazo. Salia caro: un dia que
// acaba a las 12:45 gastaba media pantalla en pintar una tarde vacia, y las
// barras se quedaban tan estrechas que no cabia ni el titulo. El dia entra
// ahora a lo ancho del panel, y para comparar ya esta la cabecera, que dice la
// primera y la ultima hora en numeros.
//
// El minimo de SPAN_MIN existe para que una unica tarea de diez minutos no se
// coma el ancho entero: un eje de 10 minutos no se lee como un dia.
export const SPAN_MIN = 2 * 60;

export function rangoHorario(tramos, minDef = HORA_MIN_DEF, maxDef = HORA_MAX_DEF) {
  if (!tramos.length) return { desde: minDef * 60, hasta: maxDef * 60 };

  let desde = MIN_DIA, hasta = 0;
  tramos.forEach(t => {
    desde = Math.min(desde, Math.floor(t.ini / 60) * 60);
    hasta = Math.max(hasta, Math.ceil(t.fin / 60) * 60);
  });

  // Se ensancha por la derecha, que es donde sobra dia; solo si choca con la
  // medianoche se tira hacia atras.
  if (hasta - desde < SPAN_MIN) {
    hasta = Math.min(desde + SPAN_MIN, MIN_DIA);
    desde = Math.max(0, Math.min(desde, hasta - SPAN_MIN));
  }
  return { desde, hasta: Math.min(hasta, MIN_DIA) };
}

// Marcas del eje. Cada cuantas horas se etiqueta depende del ancho: por debajo
// de ~46 px por hora los rotulos se tocan y el eje deja de leerse.
export function marcasHorarias(desde, hasta, ancho = 900) {
  const horas = Math.max(1, (hasta - desde) / 60);
  const paso = (ancho / horas) >= 46 ? 1 : (ancho / horas) >= 24 ? 2 : 4;
  const marcas = [];
  for (let m = Math.ceil(desde / 60) * 60; m <= hasta; m += 60) {
    const h = m / 60;
    marcas.push({ min: m, hora: h, rotulada: h % paso === 0 });
  }
  return marcas;
}

// Minutos -> porcentaje del ancho. En % y no en px para que el eje siga al
// contenedor sin medirlo: la vista vive dentro de un panel redimensionable.
export const pctDe = (min, desde, hasta) => {
  const span = hasta - desde;
  if (span <= 0) return 0;
  return Math.max(0, Math.min(100, ((min - desde) / span) * 100));
};

// Resumen de un dia: lo que va en la cabecera de su bloque.
export function resumenDia(tramos) {
  const ocupado = huecos(tramos, 0, MIN_DIA, 1)
    .reduce((s, h) => s + (h.fin - h.ini), 0);
  const span = tramos.length
    ? Math.max(...tramos.map(t => t.fin)) - Math.min(...tramos.map(t => t.ini))
    : 0;
  return {
    tareas: tramos.length,
    // Minutos con al menos una tarea encima: la suma de duraciones cuenta dos
    // veces lo que se solapa y por eso da jornadas de 14 h.
    minutosOcupados: MIN_DIA - ocupado,
    primera: tramos.length ? Math.min(...tramos.map(t => t.ini)) : null,
    ultima: tramos.length ? Math.max(...tramos.map(t => t.fin)) : null,
    span,
    solapadas: tramos.filter(t => t.solapaCon > 0).length,
  };
}

// Los dias que hay que dibujar, del mas reciente al mas antiguo.
//
// No basta con el dia al que cuelga cada entrada: una tarea de varios dias
// tiene trozo en los de en medio, y si nadie mas imputo ese dia se quedaria sin
// bloque — el despliegue del martes desapareceria del martes.
//
// El tope por entrada es una red contra fechas mal metidas: un end_date de 2030
// no debe generar mil bloques vacios.
export function diasConActividad(entradas, topePorEntrada = 31) {
  const set = new Set();
  (entradas || []).forEach(a => {
    const d0 = diaDe(a);
    if (!d0) return;
    set.add(d0);
    const d1 = dia(a.end_date);
    if (!d1 || d1 <= d0) return;
    const ms = 86400000;
    const [y, m, d] = d0.split("-").map(Number);
    let cursor = Date.UTC(y, m - 1, d);
    for (let i = 0; i < topePorEntrada; i++) {
      cursor += ms;
      const iso = new Date(cursor).toISOString().slice(0, 10);
      if (iso > d1) break;
      set.add(iso);
    }
  });
  return [...set].sort().reverse();
}

// Ancho minimo de una barra, en pixeles. Una tarea de 5 minutos en una jornada
// de diez horas mide medio pixel: es invisible, no se puede pulsar y no dice de
// que tarea es. Se le da el ancho de una marca legible aunque eso exagere un
// poco su duracion — el sitio exacto donde empieza no se toca, que es el dato
// que importa para ver con que se pisa.
export const ANCHO_MIN_BARRA = 18;

// Cuanto contenido cabe en una barra de tantos pixeles. En px y no en % porque
// el % no sabe nada del ancho del panel: el mismo 3% son 12 px en un portatil y
// 40 px en un monitor grande.
//
// Los cortes salen de lo que ocupa cada cosa: el icono 11 px mas holgura, una
// palabra corta ~34 px, un titulo con puntos suspensivos que aun se lea ~70 px,
// y el rango de horas otros ~66 px al lado del titulo.
//
// La barra ya no es el unico sitio donde se lee el nombre: la columna de la
// izquierda lo lleva entero siempre. Lo que la barra escriba es repeticion, asi
// que se es exigente con el sitio antes de recortar una palabra a la mitad.
export function nivelDetalle(px) {
  if (px >= 150) return "completo";  // icono + titulo + horas
  if (px >= 78)  return "titulo";    // icono + titulo recortado
  if (px >= 40)  return "palabra";   // icono + primera palabra
  return "marca";                    // solo el icono, o la inicial
}

// La primera palabra con la que reconocer la tarea. Se salta los numeros y los
// signos sueltos del principio ("#4 Revisar" -> "Revisar"): un "#4" no dice
// nada y es justo lo unico que cabria.
export function primeraPalabra(texto) {
  const limpio = String(texto || "").replace(/[\s:·—–-]+/g, " ").trim();
  if (!limpio) return "";
  const partes = limpio.split(" ");
  return partes.find(p => /[\p{L}]/u.test(p) && p.length > 1) || partes[0];
}

// La inicial con la que marcar una barra cuando su tipo no tiene icono. Es lo
// que evita que una barra corta sea una mancha de color sin mas.
export function inicialDe(...textos) {
  for (const t of textos) {
    const c = String(t || "").trim()[0];
    if (c && /[\p{L}\p{N}]/u.test(c)) return c.toUpperCase();
  }
  return "";
}

// ── Subtareas dentro de una barra ─────────────────────────────────────────────

// Reparte las subtareas de una entrada a lo largo del tramo de su barra.
//
// INVARIANTE que manda sobre todo lo demas: una duracion declarada se dibuja a
// escala, siempre. Nunca se estira una subtarea para rellenar el hueco que
// sobra, porque ese hueco es justo lo que se quiere ver — cuanto del tiempo de
// la tarea quedo sin repartir.
//
// Devuelve { segmentos, suma, dur, resto, modo, desborde }, con modo en
// vacio | exacto | holgado | apretado | estimado.
export function repartoSubtareas(subtareas, ini, fin) {
  const dur = Math.max(0, (fin || 0) - (ini || 0));
  const lista = Array.isArray(subtareas) ? subtareas : [];
  const vacio = { segmentos: [], suma: 0, dur, resto: dur, modo: "vacio", desborde: false };
  if (!lista.length || dur <= 0) return vacio;

  const min = s => {
    const n = Number(s?.minutos);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const declarada = lista.reduce((s, x) => s + min(x), 0);
  const mudas = lista.filter(x => !min(x)).length;

  // Nadie ha estimado nada: partes iguales cubriendo el tramo. No se sabe como
  // se repartio, pero las subtareas existen y tienen que verse.
  let duraciones;
  let modo;
  if (declarada === 0) {
    duraciones = lista.map(() => dur / lista.length);
    modo = "estimado";
  } else {
    // Las que tienen minutos se llevan los suyos; lo que sobre del tramo se
    // reparte entre las mudas. Si no sobra nada, se quedan a cero y la vista
    // las pinta como una marca fina.
    const sobra = Math.max(0, dur - declarada);
    const porMuda = mudas ? sobra / mudas : 0;
    duraciones = lista.map(s => (min(s) || porMuda));
    modo = declarada === dur ? "exacto" : declarada < dur ? "holgado" : "apretado";
  }

  // Desbordan: se escalan para caber y se dice. Nunca se sale de la barra.
  const bruto = duraciones.reduce((s, d) => s + d, 0);
  const desborde = bruto > dur;
  const factor = desborde ? dur / bruto : 1;

  // Se redondean los LIMITES, no las duraciones: asi no hay huecos de un minuto
  // entre segmentos ni un ultimo borde que se pase del tramo.
  const segmentos = [];
  let acum = 0;
  lista.forEach((sub, i) => {
    const desde = ini + Math.round(acum);
    acum += duraciones[i] * factor;
    const hasta = ini + Math.round(acum);
    segmentos.push({
      i, sub,
      ini: Math.max(ini, Math.min(desde, fin)),
      fin: Math.max(ini, Math.min(hasta, fin)),
      estimada: !min(sub),
      sinTiempo: !min(sub) && hasta - desde <= 0,
    });
  });

  return {
    segmentos,
    suma: declarada,
    dur,
    resto: Math.max(0, dur - Math.round(acum)),
    modo: desborde ? "apretado" : modo,
    desborde,
  };
}

// El reparto de un tramo ya situado, con el aviso de que la tarea no cabe
// entera en este dia: repartir la lista completa dentro de un trozo es una
// media verdad y la vista tiene que poder decirlo.
export function segmentosDeTramo(t) {
  const r = repartoSubtareas(t?.entrada?.subtareas, t?.ini, t?.fin);
  return { ...r, parcial: !!(t?.cortaAntes || t?.cortaDespues) };
}

// ── Las filas del dia ────────────────────────────────────────────────────────

// Alto de la fila de una tarea y de la de una subtarea.
export const ALTO_FILA     = 34;
export const ALTO_FILA_SUB = 22;

// El orden en que se leen las tareas del dia.
//
// Antes esto eran CARRILES: las tareas se apilaban para que las que se pisan no
// se taparan, y arriba iba la mas productiva. Con el nombre de cada tarea en su
// propia fila a la izquierda esa acrobacia sobra —nada tapa a nada— y el orden
// vuelve a ser el natural de una agenda: por la hora a la que empiezan.
//
// El peso del tipo se queda como desempate: dos que arrancan a la vez las
// separa la productividad, que es lo que decide cual era la buena.
export function ordenarFilas(tramos) {
  return [...(tramos || [])].sort((a, b) =>
    (a.ini - b.ini)
    || ((b.prod ?? 0) - (a.prod ?? 0))
    || ((b.fin - b.ini) - (a.fin - a.ini))
    || String(a?.entrada?.id).localeCompare(String(b?.entrada?.id)));
}

// Las filas que se pintan: una por tarea y, colgando de las desplegadas, una
// por subtarea. Devuelve cada fila con su `y` ya calculado y el alto total.
//
// Se acumula en vez de multiplicar porque las filas NO miden todas lo mismo: la
// de una subtarea es mas baja que la de su tarea.
export function filasDia(tramos, abiertas, alto = ALTO_FILA, altoSub = ALTO_FILA_SUB) {
  const set = abiertas instanceof Set ? abiertas : new Set(abiertas || []);
  const filas = [];
  let y = 0;
  ordenarFilas(tramos).forEach(t => {
    const tiene = !!(Array.isArray(t?.entrada?.subtareas) && t.entrada.subtareas.length);
    const abierta = tiene && set.has(String(t?.entrada?.id));
    filas.push({ clase: "tarea", clave: "t" + t?.entrada?.id, t, y, alto, tiene, abierta });
    y += alto;
    if (!abierta) return;
    segmentosDeTramo(t).segmentos.forEach(seg => {
      filas.push({ clase: "sub", clave: "s" + t?.entrada?.id + ":" + (seg.sub?.id ?? seg.i),
        t, seg, y, alto: altoSub });
      y += altoSub;
    });
  });
  return { filas, alto: y };
}
